import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import FileUploader from "@/components/FileUploader";
import { Search, ChevronDown, ChevronRight, Download, CheckCircle2, CircleDashed, FileText } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB"); } catch { return iso; }
};
const fmtSize = (b) => {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export default function WasteNotesCompleted() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({}); // { id: bool }
  const [filter, setFilter] = useState("all");  // all | completed | pending

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/waste-notes/completed/list")).data || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = rows;
    if (q) arr = arr.filter(r =>
      (r.number || "").toLowerCase().includes(q) ||
      (r.transferor_full_name || "").toLowerCase().includes(q) ||
      (r.transferee_full_name || "").toLowerCase().includes(q) ||
      (r.waste_description || "").toLowerCase().includes(q)
    );
    if (filter === "completed") arr = arr.filter(r => (r.completed_files || []).length > 0);
    if (filter === "pending")   arr = arr.filter(r => (r.completed_files || []).length === 0);
    return arr;
  }, [rows, search, filter]);

  const toggle = (id) => setExpanded(s => ({ ...s, [id]: !s[id] }));

  const completedCount = rows.filter(r => (r.completed_files || []).length > 0).length;
  const pendingCount = rows.length - completedCount;

  return (
    <div data-testid="wtn-completed-page" className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Compliance · Records</div>
        <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Completed Waste Notes</h1>
        <p className="text-sm text-slate-600 mt-1">After downloading a WTN PDF and getting it signed, upload the signed copy here for your 2-year records.</p>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="rounded-sm border border-slate-200 shadow-none p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">Total WTNs</div>
          <div className="text-2xl font-semibold mt-1 tabular-nums">{rows.length}</div>
        </Card>
        <Card className="rounded-sm border border-emerald-200 bg-emerald-50/40 shadow-none p-4">
          <div className="text-[11px] uppercase tracking-wider text-emerald-700">Signed & filed</div>
          <div className="text-2xl font-semibold mt-1 tabular-nums text-emerald-700">{completedCount}</div>
        </Card>
        <Card className="rounded-sm border border-amber-200 bg-amber-50/40 shadow-none p-4">
          <div className="text-[11px] uppercase tracking-wider text-amber-700">Awaiting upload</div>
          <div className="text-2xl font-semibold mt-1 tabular-nums text-amber-700">{pendingCount}</div>
        </Card>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 relative min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="wtn-completed-search"
              placeholder="Search by number, transferor, transferee or waste"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-sm border-slate-300"
            />
          </div>
          <div className="inline-flex rounded-sm border border-slate-300 overflow-hidden text-xs">
            {[
              ["all", "All"],
              ["pending", "Awaiting upload"],
              ["completed", "Signed & filed"],
            ].map(([v, l]) => (
              <button
                key={v}
                data-testid={`wtn-filter-${v}`}
                onClick={() => setFilter(v)}
                className={`px-3 py-2 ${filter === v ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
              >{l}</button>
            ))}
          </div>
        </div>
      </Card>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && filtered.length === 0 && (
        <Card className="rounded-sm border border-dashed border-slate-300 shadow-none p-12 text-center">
          <FileText className="w-8 h-8 text-slate-300 mx-auto" />
          <div className="text-sm text-slate-700 font-medium mt-2">No waste notes match this filter.</div>
        </Card>
      )}

      {filtered.map(r => {
        const files = r.completed_files || [];
        const done = files.length > 0;
        const isOpen = !!expanded[r.id];
        return (
          <Card key={r.id} className="rounded-sm border border-slate-200 shadow-none" data-testid={`wtn-completed-${r.id}`}>
            <button
              onClick={() => toggle(r.id)}
              className="w-full px-5 py-3 border-b border-slate-100 flex items-center justify-between text-left hover:bg-slate-50"
            >
              <div className="flex items-center gap-3">
                {isOpen
                  ? <ChevronDown className="w-4 h-4 text-slate-400" />
                  : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">{fmtDate(r.date)}</div>
                  <div className="text-sm font-medium text-slate-900">
                    <Link to={`/waste-notes/${r.id}`} className="hover:underline" onClick={(e) => e.stopPropagation()}>{r.number}</Link>
                    <span className="text-slate-500 font-normal ml-2">{r.transferor_full_name || "—"} → {r.transferee_full_name || "—"}</span>
                  </div>
                  <div className="text-xs text-slate-500 truncate max-w-[600px]">{r.waste_description || "—"}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={fileUrl(`/waste-notes/${r.id}/pdf`)}
                  download rel="noopener"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid={`wtn-completed-pdf-${r.id}`}
                ><Download className="w-3.5 h-3.5" /> Blank PDF</a>
                {done
                  ? <Badge className="rounded-sm bg-emerald-100 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {files.length} signed</Badge>
                  : <Badge className="rounded-sm bg-amber-100 text-amber-700 hover:bg-amber-100 inline-flex items-center gap-1"><CircleDashed className="w-3 h-3" /> Awaiting</Badge>}
              </div>
            </button>

            {isOpen && (
              <div className="p-5 space-y-4">
                {files.length > 0 && (
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Signed copies</div>
                    <div className="border border-slate-200 rounded-sm divide-y divide-slate-100">
                      {files.map(f => (
                        <div key={f.id} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="font-medium truncate" title={f.original_filename}>{f.original_filename}</span>
                            <span className="text-xs text-slate-500 whitespace-nowrap">{fmtSize(f.size)} · {fmtDate(f.created_at)}</span>
                          </div>
                          <a
                            href={fileUrl(`/uploads/${f.id}/download`)}
                            download rel="noopener"
                            data-testid={`wtn-file-dl-${f.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
                          ><Download className="w-3 h-3" /> Open</a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">Upload signed copy</div>
                  <FileUploader
                    entityType="wtn"
                    entityId={r.id}
                    label=""
                    accept="image/*,.pdf,.heic,.heif"
                  />
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
